import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import {
  createReminder,
  reminderDisplayAt,
  tomorrowAt,
  type ReminderQuickOptions,
  type Reminder,
  type ReminderFrequency,
  type ReminderRecurrence,
  type Weekday,
  type WeekStartsOn,
} from '../domain/reminder'
import { APP_SETTINGS } from '../config/appSettings'
import { CalendarPicker } from './CalendarPicker'
import { Icon } from './Icon'
import { useLeaveSheet } from './sheetMotion'
import { NumberStepper } from './NumberStepper'

interface ReminderEditorProps {
  noteTitle: string
  reminder: Reminder | null
  quickOptions: ReminderQuickOptions
  weekStartsOn: WeekStartsOn
  onClose: () => void
  onSave: (reminder: Reminder | null) => Promise<boolean>
}

const WEEKDAYS: ReadonlyArray<{ value: Weekday; label: string }> = [
  { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' }, { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
]
const ORDINALS = [{ value: 1, label: 'First' }, { value: 2, label: 'Second' }, { value: 3, label: 'Third' }, { value: 4, label: 'Fourth' }, { value: -1, label: 'Last' }] as const
const TIME_PICKER_HOURS = Array.from({ length: 12 }, (_, index) => index + 1)
const TIME_PICKER_MINUTES = [0, 15, 30, 45] as const
const CUSTOM_FREQUENCY_OPTIONS: ReadonlyArray<{ value: ReminderFrequency; label: string }> = [
  { value: 'daily', label: 'days' },
  { value: 'weekly', label: 'weeks' },
  { value: 'monthly', label: 'months' },
]
const CUSTOM_REPEAT_INTERVAL = APP_SETTINGS.reminders.customRepeatInterval
type PickerFocus = 'date' | 'time' | null
type ScheduleField = 'date' | 'time' | 'repeat' | null

function localDateTimeValue(value: number): { date: string; time: string } {
  const date = new Date(value)
  const offset = date.getTimezoneOffset() * 60_000
  const local = new Date(date.getTime() - offset).toISOString()
  return { date: local.slice(0, 10), time: local.slice(11, 16) }
}

function roundedUpToQuarterHour(value: number): number {
  const next = new Date(value)
  next.setSeconds(0, 0)
  next.setMinutes(Math.ceil(next.getMinutes() / 15) * 15)
  return next.getTime()
}

function fromLocalDateTime(date: string, time: string): number | null {
  if (!date || !time) return null
  const result = new Date(`${date}T${time}`)
  return Number.isFinite(result.getTime()) ? result.getTime() : null
}

function repeatMode(reminder: Reminder | null): ReminderFrequency | 'never' | 'custom' {
  const recurrence = reminder?.recurrence
  if (!recurrence) return 'never'
  return recurrence.interval === 1 && (recurrence.frequency !== 'monthly' || recurrence.monthlyRule?.kind === 'day-of-month')
    ? recurrence.frequency
    : 'custom'
}

function timeParts(value: string): { hour24: number; minute: number } {
  const [hour = '0', minute = '0'] = value.split(':')
  return {
    hour24: Math.max(0, Math.min(23, Number(hour) || 0)),
    minute: Math.max(0, Math.min(59, Number(minute) || 0)),
  }
}

function timeValue(hour24: number, minute: number): string {
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function customRepeatInterval(value: number): number {
  return Math.min(CUSTOM_REPEAT_INTERVAL.max, Math.max(CUSTOM_REPEAT_INTERVAL.min, value))
}

function naturalDateLabel(value: string): string {
  const [year = 0, month = 0, day = 0] = value.split('-').map(Number)
  const selected = new Date(year, month - 1, day, 12)
  const today = new Date()
  const selectedDay = Date.UTC(selected.getFullYear(), selected.getMonth(), selected.getDate())
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  const dayDistance = Math.round((selectedDay - todayDay) / (24 * 60 * 60 * 1_000))
  if (dayDistance === 0) return 'Today'
  if (dayDistance === 1) return 'Tomorrow'
  if (dayDistance === -1) return 'Yesterday'
  if (dayDistance > 1 && dayDistance < 7) {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(selected)
  }
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(selected)
}

export function ReminderEditor({ noteTitle, reminder, quickOptions, weekStartsOn, onClose, onSave }: ReminderEditorProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const leave = useLeaveSheet(dialogRef, onClose)
  const titleId = useId()
  const reminderNextAt = reminder ? reminderDisplayAt(reminder) : null
  const initial = useMemo(() => localDateTimeValue(reminderNextAt ?? roundedUpToQuarterHour(Date.now())), [reminderNextAt])
  const initialRecurrence = reminder?.recurrence
  const [activeField, setActiveField] = useState<ScheduleField>(() => {
    const initialRepeat = repeatMode(reminder)
    return initialRepeat === 'weekly' || initialRepeat === 'custom' ? 'repeat' : null
  })
  const [date, setDate] = useState(initial.date)
  const [time, setTime] = useState(initial.time)
  const [dateSelected, setDateSelected] = useState(Boolean(reminder))
  const [timeSelected, setTimeSelected] = useState(Boolean(reminder))
  const [customOpen, setCustomOpen] = useState(Boolean(reminder))
  const [pickerFocus, setPickerFocus] = useState<PickerFocus>(null)
  const [repeat, setRepeat] = useState<ReminderFrequency | 'never' | 'custom'>(repeatMode(reminder))
  const [customFrequency, setCustomFrequency] = useState<ReminderFrequency>(initialRecurrence?.frequency ?? 'daily')
  const [interval, setInterval] = useState(() => customRepeatInterval(initialRecurrence?.interval ?? CUSTOM_REPEAT_INTERVAL.min))
  const [weekdays, setWeekdays] = useState<Weekday[]>(initialRecurrence?.weekdays ?? [new Date(reminderNextAt ?? Date.now()).getDay() as Weekday])
  const [monthlyOrdinal, setMonthlyOrdinal] = useState(initialRecurrence?.monthlyRule?.kind === 'ordinal-weekday')
  const [ordinal, setOrdinal] = useState<1 | 2 | 3 | 4 | -1>(initialRecurrence?.monthlyRule?.kind === 'ordinal-weekday' ? initialRecurrence.monthlyRule.ordinal : 1)
  const [ordinalWeekday, setOrdinalWeekday] = useState<Weekday>(initialRecurrence?.monthlyRule?.kind === 'ordinal-weekday' ? initialRecurrence.monthlyRule.weekday : new Date(reminderNextAt ?? Date.now()).getDay() as Weekday)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const effectiveNextAt = reminder ? reminderDisplayAt(reminder) : roundedUpToQuarterHour(Date.now())
    const next = localDateTimeValue(effectiveNextAt)
    const recurrence = reminder?.recurrence
    setDate(next.date)
    setTime(next.time)
    setDateSelected(Boolean(reminder))
    setTimeSelected(Boolean(reminder))
    setCustomOpen(Boolean(reminder))
    const nextRepeat = repeatMode(reminder)
    setActiveField(nextRepeat === 'weekly' || nextRepeat === 'custom' ? 'repeat' : null)
    setPickerFocus(null)
    setRepeat(repeatMode(reminder))
    setCustomFrequency(recurrence?.frequency ?? 'daily')
    setInterval(customRepeatInterval(recurrence?.interval ?? CUSTOM_REPEAT_INTERVAL.min))
    setWeekdays(recurrence?.weekdays ?? [new Date(effectiveNextAt).getDay() as Weekday])
    setMonthlyOrdinal(recurrence?.monthlyRule?.kind === 'ordinal-weekday')
    setOrdinal(recurrence?.monthlyRule?.kind === 'ordinal-weekday' ? recurrence.monthlyRule.ordinal : 1)
    setOrdinalWeekday(recurrence?.monthlyRule?.kind === 'ordinal-weekday' ? recurrence.monthlyRule.weekday : new Date(effectiveNextAt).getDay() as Weekday)
    setError(null)
  }, [noteTitle, reminder])

  const recurrenceFor = (nextAt: number): ReminderRecurrence | null => {
    const frequency = repeat === 'custom' ? customFrequency : repeat
    if (frequency === 'never') return null
    const nextInterval = repeat === 'custom' ? interval : 1
    if (frequency === 'daily') return { frequency, interval: nextInterval }
    if (frequency === 'weekly') return { frequency, interval: nextInterval, weekdays: weekdays.length > 0 ? weekdays : [new Date(nextAt).getDay() as Weekday] }
    return {
      frequency,
      interval: nextInterval,
      monthlyRule: monthlyOrdinal
        ? { kind: 'ordinal-weekday', ordinal, weekday: ordinalWeekday }
        : { kind: 'day-of-month', day: new Date(nextAt).getDate() },
    }
  }

  const saveInBackground = (nextReminder: Reminder | null) => {
    // The app-level callback owns durable success/error feedback. Dismiss before any
    // persistence, permission, synchronization, or native scheduling work can await.
    leave()
    void onSave(nextReminder).catch(() => undefined)
  }

  const saveAt = (nextAt: number) => {
    if (nextAt <= Date.now()) {
      setError('Choose a future date and time.')
      return
    }
    setError(null)
    try {
      const created = createReminder(nextAt, { title: reminder?.title || noteTitle, recurrence: recurrenceFor(nextAt), timeZone: reminder?.timeZone })
      const nextReminder = reminder ? { ...created, createdAt: reminder.createdAt } : created
      saveInBackground(nextReminder)
    } catch {
      setError('Choose a valid reminder schedule.')
    }
  }

  const chooseQuick = (nextAt: number) => { saveAt(nextAt) }

  const updateWeekday = (weekday: Weekday) => {
    setWeekdays((current) => current.includes(weekday) ? current.filter((value) => value !== weekday) : [...current, weekday].sort())
  }

  const activeFrequency = repeat === 'custom' ? customFrequency : repeat
  const repeatLabel = activeFrequency === 'never' ? 'Once' : `${activeFrequency[0]!.toUpperCase()}${activeFrequency.slice(1)}`
  const saveLabel = reminder ? 'Update reminder' : 'Set reminder'
  const { hour24, minute } = timeParts(time)
  const hour12 = hour24 % 12 || 12
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const orderedWeekdays = useMemo(
    () => weekStartsOn === 'sunday' ? WEEKDAYS : [...WEEKDAYS.slice(1), WEEKDAYS[0]!],
    [weekStartsOn],
  )
  const openPicker = (nextPicker: Exclude<PickerFocus, null>) => setPickerFocus(nextPicker)

  const closePicker = () => setPickerFocus(null)

  const chooseRepeatOption = (nextRepeat: ReminderFrequency | 'custom') => {
    setRepeat(nextRepeat)
    setActiveField(nextRepeat === 'weekly' || nextRepeat === 'custom' ? 'repeat' : null)
  }

  const setPickerHour = (nextHour12: number) => {
    const nextHour24 = (nextHour12 % 12) + (period === 'PM' ? 12 : 0)
    setTime(timeValue(nextHour24, minute))
  }

  const setPickerMinute = (nextMinute: number) => {
    setTime(timeValue(hour24, nextMinute))
  }

  const setPickerPeriod = (nextPeriod: 'AM' | 'PM') => {
    setTime(timeValue((hour12 % 12) + (nextPeriod === 'PM' ? 12 : 0), minute))
  }

  const chooseCalendarDate = (nextDateValue: string) => {
    setDate(nextDateValue)
    const nextAt = fromLocalDateTime(nextDateValue, time)
    if (nextAt && nextAt > Date.now() && error === 'Choose a future date and time.') setError(null)
  }

  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
    else dialog.setAttribute('open', '')
    return () => {
      if (!dialog.open) return
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])

  const saveScheduledReminder = () => {
    if (!dateSelected || !timeSelected) {
      setError('Choose both a date and time.')
      return
    }
    const nextAt = fromLocalDateTime(date, time)
    if (!nextAt) {
      setError('Choose a valid date and time.')
      return
    }
    saveAt(nextAt)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    leave()
  }

  const openScheduleField = (field: Exclude<ScheduleField, null>) => {
    setCustomOpen(true)
    if (field === 'date' || field === 'time') {
      openPicker(field)
      return
    }
    setActiveField((current) => current === field ? null : field)
  }

  const confirmPicker = () => {
    if (pickerFocus === 'date') {
      setDateSelected(true)
      if (!timeSelected) {
        openPicker('time')
        return
      }
    }
    if (pickerFocus === 'time') {
      setTimeSelected(true)
      if (!dateSelected) {
        openPicker('date')
        return
      }
    }
    closePicker()
  }

  const scheduleFieldIsActive = (field: Exclude<ScheduleField, null>) => (
    activeField === field || pickerFocus === field
  )
  const repeatEnabled = repeat !== 'never'
  const tomorrowQuickTime = new Intl.DateTimeFormat(undefined, { hour: 'numeric' })
    .format(new Date(2000, 0, 1, quickOptions.tomorrowHour))

  const toggleCustomSchedule = () => {
    if (customOpen) {
      setActiveField(null)
      closePicker()
    }
    setCustomOpen(!customOpen)
  }

  const toggleRepeat = (enabled: boolean) => {
    setRepeat(enabled ? (repeat === 'never' ? 'daily' : repeat) : 'never')
    setActiveField(null)
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className="modal modal-bottom sm:modal-middle studio-sheet reminder-modal"
      aria-labelledby={titleId}
      onKeyDownCapture={handleKeyDown}
      onCancel={(event) => {
        event.preventDefault()
        leave()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) leave()
      }}
    >
      <section className="modal-box sheet-panel reminder-editor">
        <header className="sheet-header">
          <h2 id={titleId} className="sheet-title"><Icon name="bell" />Remind me</h2>
          <button className="btn btn-ghost btn-square sheet-close" type="button" onClick={leave} aria-label="Close reminder editor"><Icon name="close" /></button>
        </header>
        <div className="reminder-editor__body">
          <div className="reminder-editor__body-content sheet-body" inert={pickerFocus ? true : undefined}>
            <section className={`reminder-editor__method reminder-editor__method--quick${customOpen ? ' is-collapsed' : ''}`} aria-labelledby={`${titleId}-quick`}>
              <h3 id={`${titleId}-quick`} className="sheet-heading"><button className="btn btn-ghost reminder-editor__quick-toggle" type="button" onClick={toggleCustomSchedule} aria-expanded={!customOpen} aria-controls={`${titleId}-custom-panel`}><Icon className="reminder-editor__method-toggle-icon" name="chevron" />Quick pick</button></h3>
              <div className="reminder-editor__quick" aria-label="Quick reminder times">
                <button className="btn reminder-editor__quick-action" type="button" onClick={() => chooseQuick(Date.now() + quickOptions.firstMinutes * 60_000)}>{quickOptions.firstMinutes >= 60 ? `${quickOptions.firstMinutes / 60} hr${quickOptions.firstMinutes === 60 ? '' : 's'}` : `${quickOptions.firstMinutes} min`}</button>
                <button className="btn reminder-editor__quick-action" type="button" onClick={() => chooseQuick(Date.now() + quickOptions.secondMinutes * 60_000)}>{quickOptions.secondMinutes >= 60 ? `${quickOptions.secondMinutes / 60} hr${quickOptions.secondMinutes === 60 ? '' : 's'}` : `${quickOptions.secondMinutes} min`}</button>
                <button className="btn reminder-editor__quick-action reminder-editor__quick-action--tomorrow" type="button" onClick={() => chooseQuick(tomorrowAt(quickOptions.tomorrowHour))}><span>Tomorrow</span><small>{tomorrowQuickTime}</small></button>
              </div>
            </section>
            <section className="reminder-editor__method reminder-editor__method--custom" aria-labelledby={`${titleId}-custom`}>
              <h3 id={`${titleId}-custom`} className="sheet-heading"><button className="btn btn-ghost reminder-editor__specific-toggle" type="button" onClick={toggleCustomSchedule} aria-expanded={customOpen} aria-controls={`${titleId}-custom-panel`}><Icon className="reminder-editor__method-toggle-icon" name="chevron" />Specific</button></h3>
              <div className={`reminder-editor__custom-schedule${customOpen ? ' is-open' : ''}`}>
                <div id={`${titleId}-custom-panel`} className="reminder-editor__custom-panel" aria-hidden={!customOpen} inert={customOpen ? undefined : true}>
                <div className="reminder-editor__custom-panel-content">
                <div className="reminder-editor__schedule-controls" aria-label="Custom reminder schedule">
                  <button className={`btn reminder-editor__schedule-control${dateSelected ? ' is-selected' : ''}${scheduleFieldIsActive('date') ? ' is-active' : ''}`} type="button" onClick={() => openScheduleField('date')} aria-label={`Date: ${dateSelected ? naturalDateLabel(date) : 'not set'}`} aria-expanded={scheduleFieldIsActive('date')}>
                    <Icon name="calendar" /><span><strong>{dateSelected ? naturalDateLabel(date) : 'Date'}</strong></span>
                  </button>
                  <button className={`btn reminder-editor__schedule-control${timeSelected ? ' is-selected' : ''}${scheduleFieldIsActive('time') ? ' is-active' : ''}`} type="button" onClick={() => openScheduleField('time')} aria-label={`Time: ${timeSelected ? `${hour12}:${String(minute).padStart(2, '0')} ${period}` : 'not set'}`} aria-expanded={scheduleFieldIsActive('time')}>
                    <Icon name="clock" /><span><strong>{timeSelected ? `${hour12}:${String(minute).padStart(2, '0')} ${period}` : 'Time'}</strong></span>
                  </button>
                </div>
                <div className="reminder-editor__repeat-summary">
                  <span className="reminder-editor__repeat-heading"><Icon name="repeat-circular" /><span><strong>Repeat</strong><small>{repeatLabel}</small></span></span>
                  <input className="toggle toggle-primary reminder-editor__repeat-toggle" type="checkbox" checked={repeatEnabled} onChange={(event) => toggleRepeat(event.target.checked)} aria-label="Repeat reminder" />
                </div>
                {repeatEnabled ? <div className="reminder-editor__repeat-controls">
                  <div className="reminder-editor__repeat-presets" role="group" aria-label="Repeat schedule">
                    {(['daily', 'weekly', 'monthly', 'custom'] as const).map((option) => <button key={option} className={`btn reminder-editor__repeat-option${repeat === option ? ' is-selected' : ''}`} type="button" onClick={() => chooseRepeatOption(option)} aria-pressed={repeat === option}>{option === 'custom' ? 'Custom' : `${option[0]!.toUpperCase()}${option.slice(1)}`}</button>)}
                  </div>
                </div> : null}
                {activeField === 'repeat' ? <section className="card card-border reminder-editor__expanded-panel reminder-editor__expanded-panel--repeat reminder-editor__recurrence" aria-label="Custom repeat options">
                  {repeat === 'custom' ? <fieldset className="fieldset reminder-editor__field"><label className="fieldset-legend" htmlFor="reminder-interval">Every</label><span className="reminder-editor__interval"><NumberStepper id="reminder-interval" className="reminder-editor__interval-stepper" ariaLabel="Repeat interval" decrementLabel="Decrease repeat interval" incrementLabel="Increase repeat interval" value={interval} min={CUSTOM_REPEAT_INTERVAL.min} max={CUSTOM_REPEAT_INTERVAL.max} onChange={setInterval} /><span className="reminder-editor__custom-frequency" role="group" aria-label="Custom repetition frequency">{CUSTOM_FREQUENCY_OPTIONS.map((option) => <button key={option.value} className={`btn reminder-editor__custom-frequency-option${customFrequency === option.value ? ' is-selected' : ''}`} type="button" onClick={() => setCustomFrequency(option.value)} aria-pressed={customFrequency === option.value}>{option.label}</button>)}</span></span></fieldset> : null}
                  {activeFrequency === 'weekly' ? <fieldset className="fieldset reminder-editor__weekdays"><legend className="fieldset-legend">On</legend>{orderedWeekdays.map((weekday) => <label className="sheet-choice reminder-editor__weekday-option" key={weekday.value}><input className="checkbox checkbox-primary" type="checkbox" checked={weekdays.includes(weekday.value)} onChange={() => updateWeekday(weekday.value)} />{weekday.label}</label>)}</fieldset> : null}
                  {activeFrequency === 'monthly' && repeat === 'custom' ? <><label className="reminder-editor__toggle"><input className="checkbox checkbox-primary" type="checkbox" checked={monthlyOrdinal} onChange={(event) => setMonthlyOrdinal(event.target.checked)} />On an ordinal weekday</label>{monthlyOrdinal ? <fieldset className="fieldset reminder-editor__field"><label className="fieldset-legend" htmlFor="reminder-ordinal">Monthly rule</label><span className="reminder-editor__interval"><select id="reminder-ordinal" className="select" value={ordinal} onChange={(event) => setOrdinal(Number(event.target.value) as 1 | 2 | 3 | 4 | -1)}>{ORDINALS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><select className="select" aria-label="Ordinal weekday" value={ordinalWeekday} onChange={(event) => setOrdinalWeekday(Number(event.target.value) as Weekday)}>{orderedWeekdays.map((weekday) => <option key={weekday.value} value={weekday.value}>{weekday.label}</option>)}</select></span></fieldset> : null}</> : null}
                </section> : null}
                </div>
                </div>
              </div>
            </section>
          {error ? <p className="form-validation-message reminder-editor__error" role="alert">{error}</p> : null}
          </div>
        {pickerFocus ? (
          <section className={`reminder-editor__picker-layer visual-viewport-frame reminder-editor__picker-layer--${pickerFocus}`} role="dialog" aria-modal="true" aria-label={pickerFocus === 'time' ? 'Choose reminder time' : 'Choose reminder date'}>
            <div className="reminder-editor__picker-content">
              {pickerFocus === 'time' ? (
                <section className="reminder-editor__time-picker reminder-editor__time-picker--focus">
                  <div className="reminder-editor__time-main">
                    <div className="reminder-editor__time-display" aria-label={`Edit time, currently ${hour12}:${String(minute).padStart(2, '0')} ${period}`}><span>{hour12}</span><span className="reminder-editor__time-colon">:</span><span>{String(minute).padStart(2, '0')}</span><div className="join join-vertical reminder-editor__period-toggle" role="group" aria-label="Time period">{(['AM', 'PM'] as const).map((pickerPeriod) => <button key={pickerPeriod} className={`btn btn-ghost btn-xs join-item${pickerPeriod === period ? ' is-selected' : ''}`} type="button" onClick={() => setPickerPeriod(pickerPeriod)} aria-pressed={pickerPeriod === period}>{pickerPeriod}</button>)}</div></div>
                    <div className="reminder-editor__time-choice-grid">
                      <div className="reminder-editor__time-choice-group"><span className="reminder-editor__time-choice-label">Hour</span><div className="reminder-editor__hour-grid" aria-label="Choose hour">{TIME_PICKER_HOURS.map((pickerHour) => <button key={pickerHour} className={`btn reminder-editor__hour${pickerHour === hour12 ? ' is-selected' : ''}`} type="button" onClick={() => setPickerHour(pickerHour)} aria-pressed={pickerHour === hour12}>{pickerHour}</button>)}</div></div>
                      <div className="reminder-editor__time-choice-group"><span className="reminder-editor__time-choice-label">Minutes</span><div className="reminder-editor__minute-grid" aria-label="Choose minutes">{TIME_PICKER_MINUTES.map((pickerMinute) => <button key={pickerMinute} className={`btn reminder-editor__minute${pickerMinute === minute ? ' is-selected' : ''}`} type="button" onClick={() => setPickerMinute(pickerMinute)} aria-pressed={pickerMinute === minute}>:{String(pickerMinute).padStart(2, '0')}</button>)}</div></div>
                    </div>
                  </div>
                  <button className="btn btn-primary reminder-editor__set-time" type="button" onClick={confirmPicker}>Set time</button>
                </section>
              ) : (
                <CalendarPicker value={date} weekStartsOn={weekStartsOn} label="Choose reminder date" onChange={chooseCalendarDate} onConfirm={confirmPicker} />
              )}
            </div>
          </section>
        ) : null}
        </div>
        {!pickerFocus ? <div className="sheet-footer">
          {reminder ? <button type="button" className="btn btn-error reminder-editor__remove" onClick={() => saveInBackground(null)}><Icon name="bell-off" />Remove</button> : null}
          <button type="button" className="btn btn-primary reminder-editor__save" onClick={saveScheduledReminder}>{saveLabel}</button>
        </div> : null}
      </section>
    </dialog>,
    document.body,
  )
}
